/**
 * Image variant + transform URL helpers (web).
 *
 * Must stay in sync with the mobile equivalent at
 * services/mobile-app/src/utils/imageUrl.ts and the Cloudflare Worker's ALLOWED table.
 */

export type ImageVariant = "avatar" | "thumb" | "detail" | "full";

const VARIANT_SIZES: Record<ImageVariant, number[]> = {
  avatar: [96, 192],
  thumb: [400, 800],
  detail: [800, 1600],
  full: [2000],
};

function getCdnBase(): string | null {
  const base = import.meta.env.VITE_IMAGE_CDN_BASE_URL as string | undefined;
  if (!base) return null;
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

function pickSize(variant: ImageVariant, renderedWidth: number, dpr = 2): number {
  const target = renderedWidth * dpr;
  const sizes = VARIANT_SIZES[variant];
  for (const s of sizes) {
    if (s >= target) return s;
  }
  return sizes[sizes.length - 1];
}

/**
 * Build a transformed image URL for the given source + variant.
 * Returns `src` unchanged when CDN base is unset (feature flag) or when `src`
 * doesn't point at our CDN domain (legacy/external images).
 */
export function buildImageUrl(
  src: string | null | undefined,
  variant: ImageVariant,
  renderedWidth: number,
  dpr = 2,
): string | null {
  if (!src) return null;
  const base = getCdnBase();
  if (!base) return src;
  if (!src.startsWith(base + "/")) return src;

  const key = src.slice(base.length + 1);
  const size = pickSize(variant, renderedWidth, dpr);
  return `${base}/v/${variant}/${size}/${key}`;
}

/**
 * Return a srcSet string with 1x/2x variants for responsive loading.
 */
export function buildSrcSet(
  src: string | null | undefined,
  variant: ImageVariant,
  renderedWidth: number,
): string | null {
  const x1 = buildImageUrl(src, variant, renderedWidth, 1);
  const x2 = buildImageUrl(src, variant, renderedWidth, 2);
  if (!x1 || !x2 || x1 === x2) return x1 ? `${x1}` : null;
  return `${x1} 1x, ${x2} 2x`;
}
